import React, { useEffect, useMemo, useState } from 'react';
import { Box, FormControlLabel, MenuItem, Switch, TextField } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { PrintTemplate } from '../../services/printTemplateService';
import { colors } from '../../theme/designSystem';

/**
 * Escolha do template na hora de imprimir (item 123, pedido da cliente Kátia): o padrão da
 * entidade vem pré-selecionado, qualquer template ativo pode ser escolhido, e "Sem template"
 * imprime só o conteúdo — sem logo, cabeçalho, rodapé E sem as linhas de separação, que são
 * desenhadas pelo componente e não pelo template (um template vazio deixaria os traços).
 *
 * A escolha fica na sessionStorage por tela: quem imprime dez recibos seguidos não reescolhe.
 * Nada disto aparece no papel: a barra some no @media print.
 */
export interface PrintTemplateChoice {
  templates: PrintTemplate[];
  /** Template em uso; nulo = sem template (ou nenhum cadastrado) */
  template: PrintTemplate | null;
  templateId: string;
  noTemplate: boolean;
  setTemplateId: (id: string) => void;
  setNoTemplate: (value: boolean) => void;
}

const STORAGE_PREFIX = 'clinic4us-print-template:';

const readStored = (key: string): { templateId?: string; noTemplate?: boolean } => {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_PREFIX + key) || '{}');
  } catch {
    return {};
  }
};

export function usePrintTemplateChoice(templates: PrintTemplate[], storageKey: string): PrintTemplateChoice {
  const active = useMemo(() => templates.filter((t) => t.status), [templates]);
  const defaultId = useMemo(() => (active.find((t) => t.isDefault) ?? active[0])?.id ?? '', [active]);
  const [templateId, setTemplateIdState] = useState<string>('');
  const [noTemplate, setNoTemplateState] = useState<boolean>(() => readStored(storageKey).noTemplate === true);

  // Quando os templates chegam: a escolha guardada vale se ainda existir; senão, o padrão da entidade
  useEffect(() => {
    const stored = readStored(storageKey).templateId;
    setTemplateIdState(stored && active.some((t) => t.id === stored) ? stored : defaultId);
  }, [active, defaultId, storageKey]);

  const persist = (id: string, off: boolean) => {
    try { sessionStorage.setItem(STORAGE_PREFIX + storageKey, JSON.stringify({ templateId: id, noTemplate: off })); } catch { /* sem storage */ }
  };

  const setTemplateId = (id: string) => { setTemplateIdState(id); persist(id, noTemplate); };
  const setNoTemplate = (value: boolean) => { setNoTemplateState(value); persist(templateId, value); };

  const template = noTemplate ? null : (active.find((t) => t.id === templateId) ?? active.find((t) => t.id === defaultId) ?? null);

  return { templates: active, template, templateId, noTemplate, setTemplateId, setNoTemplate };
}

interface BarProps {
  choice: PrintTemplateChoice;
  sx?: SxProps<Theme>;
}

/** Seletor + "Sem template", para a barra da impressão. Some sem nenhum template cadastrado e no papel. */
export const PrintTemplateBar: React.FC<BarProps> = ({ choice, sx }) => {
  if (choice.templates.length === 0) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mr: 'auto', '@media print': { display: 'none' }, ...(sx as object) }}>
      <TextField
        select
        size="small"
        label="Template"
        value={choice.templateId}
        onChange={(e) => choice.setTemplateId(e.target.value)}
        disabled={choice.noTemplate}
        InputLabelProps={{ shrink: true }}
        sx={{ minWidth: 220, backgroundColor: colors.white }}
      >
        {choice.templates.map((t) => (
          <MenuItem key={t.id} value={t.id}>{t.title}{t.isDefault ? ' (padrão)' : ''}</MenuItem>
        ))}
      </TextField>
      <FormControlLabel
        control={<Switch size="small" checked={choice.noTemplate} onChange={(e) => choice.setNoTemplate(e.target.checked)} />}
        label="Sem template"
        title="Imprime só o conteúdo: sem logo, cabeçalho, rodapé e linhas de separação"
        sx={{ ml: 0.5, '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
      />
    </Box>
  );
};
